import { useState, useEffect } from 'react';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import { Modal } from '@shared/components/modals/Modal';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { dataGridCol } from '@shared/components/grid/common';
import { formatPercentage } from '@shared/commonFunctions';
import { IAssetOnReports, IAssetReports } from '../interfaces';
import { getAssetOnReport } from '../../../../Assets/requests';

const formatCellValue = (value: string | number | undefined) => {
    if (value === undefined || value === null) return '–';
    if (value === 'Y' || value === 'N') return value;
    const numericValue = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(numericValue) ? formatPercentage(numericValue) : value;
  };

const columns: GridColDef[] = [
  dataGridCol('condition'),
  dataGridCol('TU', { valueFormatter: ({ value }) => formatCellValue(value) }),
  dataGridCol('EXP', { valueFormatter: ({ value }) => formatCellValue(value) }),
  dataGridCol('EQ', { valueFormatter: ({ value }) => formatCellValue(value) }),
].map((column) => ({ flex: 1, ...column }));

export const AssetOnReportsModal = ({ assetId, onClose }: IAssetOnReports) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<IAssetReports[]>([
    { condition: 'Lien Holder' },
    { condition: 'Rate' },
    { condition: 'Payment' },
    { condition: 'Balance' },
    { condition: 'Account Number' },
    { condition: 'Asset on Report' },
    { condition: 'Match Score' },
  ]);

  const getBureauColumnKey = (bureau: string): 'EXP' | 'TU' | 'EQ' | null => {
    switch (bureau.toLowerCase()) {
      case 'experian':
        return 'EXP';
      case 'transunion':
        return 'TU';
      case 'equifax':
        return 'EQ';
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        const response = await getAssetOnReport(assetId);

        if (response && response.searchResults) {
          const tableRows = [...rows];

          response.searchResults.forEach(({ bureauType, matchingRecords }) => {
            const columnKey = getBureauColumnKey(bureauType);
            if (!columnKey) return;

            if (!matchingRecords || matchingRecords.length === 0) {
              tableRows[0][columnKey] = 0;
              tableRows[1][columnKey] = 0;
              tableRows[2][columnKey] = 0;
              tableRows[3][columnKey] = 0;
              tableRows[4][columnKey] = 0;
              tableRows[5][columnKey] = 'N';
              tableRows[6][columnKey] = 0;
              return;
            }

            const highestRecord = matchingRecords.reduce((prev, current) =>
              prev.overallScore > current.overallScore ? prev : current
            );

            const getFieldScore = (fieldName: string): number => {
              const fieldDetail = highestRecord.fieldScoreDetails.find((detail) => detail.fieldName === fieldName);

              return fieldDetail ? fieldDetail.matchingScore * 100 : 0;
            };

            const assetOnReportValue = (score: number | undefined): string => {
              if (score === undefined) {
                return 'No';
              }
              if (score >= 0.86) {
                return 'Yes';
              }
              if (score >= 0.7) {
                return 'Likely';
              }
              if (score >= 0.41) {
                return 'Maybe';
              }
              return 'No';
            };

            tableRows[0][columnKey] = getFieldScore('CreditorName');
            tableRows[1][columnKey] = getFieldScore('EstApr');
            tableRows[2][columnKey] = getFieldScore('Payment');
            tableRows[3][columnKey] = getFieldScore('Balance');
            tableRows[4][columnKey] = getFieldScore('AccountNumber');
            tableRows[5][columnKey] = assetOnReportValue(highestRecord.overallScore);
            tableRows[6][columnKey] = highestRecord.overallScore * 100;
          });

          setRows(tableRows);
        }
      } catch (error) {
        console.error('Error fetching asset report:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [assetId]);

  return (
    <Modal
      open
      hideBackdrop
      dialogMinWidth="50vw"
      title="Asset on Reports"
      onClose={onClose}
    >
      <Box>
        <PhoenixGrid
          noButton
          strict
          autoHeight
          hideFooter
          canNotEdit
          canNotCopy
          name="asset-reports-grid"
          loading={loading}
          rows={rows}
          getRowId={(row: IAssetReports) => (row.condition ?? '') as GridRowId}
          columns={columns}
        />
      </Box>
    </Modal>
  );
};
